import React from 'react';
import { GlassButton } from '@/components/ui/GlassButton';
import { GlassCard } from '@/components/ui/GlassCard';
import { GlassPanel } from '@/components/ui/GlassPanel';
import { GlassInput, GlassSelect, GlassTextarea } from '@/components/ui/GlassInput';
import { GlassBadge } from '@/components/ui/GlassBadge';
import { GlassStatCard } from '@/components/ui/GlassStatCard';
import { GlassNavbar } from '@/components/ui/GlassNavbar';
import {
  FiArrowRight, FiDownload, FiSend, FiEye, FiPlus,
  FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiClock,
  FiGrid, FiBriefcase, FiBarChart2, FiSettings, FiSearch,
} from 'react-icons/fi';

/* ─── Section header ─── */
const SectionTitle = ({ children }) => (
  <div className="w-full max-w-5xl">
    <h2 className="text-xl font-semibold text-white/90 tracking-wide mb-1">{children}</h2>
    <div className="glass-divider glass-divider--glow" />
  </div>
);

const GlassButtonDemo = () => {
  return (
    <div
      className="min-h-screen flex flex-col items-center gap-14 py-16 px-6"
      style={{ background: 'linear-gradient(135deg, #0d0024 0%, #1a1040 40%, #0a1628 70%, #0d0024 100%)' }}
    >
      {/* ════════ Title ════════ */}
      <div className="text-center mb-4">
        <h1 className="text-4xl font-bold text-white tracking-wide mb-2">
          Glassmorphism Design System
        </h1>
        <p className="text-white/40 text-sm max-w-lg mx-auto">
          Frosted glass UI components styled with Namibian brand colours — designed for dark surfaces.
        </p>
      </div>

      {/* ════════ Navbar ════════ */}
      <SectionTitle>Navigation Bar</SectionTitle>
      <GlassNavbar
        items={[
          { label: 'Dashboard', icon: FiGrid },
          { label: 'Projects', icon: FiBriefcase },
          { label: 'Reports', icon: FiBarChart2 },
          { label: 'Settings', icon: FiSettings },
        ]}
      />

      {/* ════════ Stat Cards ════════ */}
      <SectionTitle>Stat Cards</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 w-full max-w-5xl">
        <GlassStatCard
          title="Projects On Track"
          value="47"
          subtitle="of 62 total"
          icon={FiCheckCircle}
          accent="green"
          trend={12}
        />
        <GlassStatCard
          title="At Risk"
          value="9"
          subtitle="requires attention"
          icon={FiAlertTriangle}
          accent="gold"
          trend={-3}
        />
        <GlassStatCard
          title="Critical Delays"
          value="6"
          subtitle="overdue milestones"
          icon={FiClock}
          accent="red"
          trend={-8}
        />
        <GlassStatCard
          title="Budget Utilisation"
          value="73%"
          subtitle="N$ 2.4B allocated"
          icon={FiTrendingUp}
          accent="blue"
          trend={5}
        />
      </div>

      {/* ════════ Glass Cards ════════ */}
      <SectionTitle>Cards</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl">
        <GlassCard accent="blue" title="Ministry Update">
          <p className="text-sm mb-3">Transport infrastructure programme reached 68% completion this quarter.</p>
          <div className="glass-progress">
            <div className="glass-progress__fill glass-progress__fill--blue" style={{ width: '68%' }} />
          </div>
          <p className="text-xs text-white/40 mt-2">Updated 2 hours ago</p>
        </GlassCard>

        <GlassCard accent="green" title="Compliance Status">
          <div className="flex flex-wrap gap-2 mb-3">
            <GlassBadge glow="green" size="sm">On Track</GlassBadge>
            <GlassBadge glow="gold" size="sm">Review</GlassBadge>
            <GlassBadge glow="red" size="sm" pulse>Overdue</GlassBadge>
          </div>
          <p className="text-sm">12 of 15 deliverables submitted on time.</p>
        </GlassCard>

        <GlassCard accent="gold" title="Budget Summary">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Education</span>
              <span className="text-white/90 font-medium">N$ 840M</span>
            </div>
            <div className="glass-progress">
              <div className="glass-progress__fill glass-progress__fill--gold" style={{ width: '84%' }} />
            </div>
            <div className="flex justify-between text-sm">
              <span>Health</span>
              <span className="text-white/90 font-medium">N$ 620M</span>
            </div>
            <div className="glass-progress">
              <div className="glass-progress__fill glass-progress__fill--green" style={{ width: '62%' }} />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* ════════ Badges ════════ */}
      <SectionTitle>Badges</SectionTitle>
      <div className="flex flex-wrap justify-center gap-4">
        <GlassBadge glow="green">On Track</GlassBadge>
        <GlassBadge glow="gold">At Risk</GlassBadge>
        <GlassBadge glow="red">Delayed</GlassBadge>
        <GlassBadge glow="blue">In Progress</GlassBadge>
        <GlassBadge glow="navy">Pending</GlassBadge>
        <GlassBadge glow="white">Neutral</GlassBadge>
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        <GlassBadge glow="red" size="sm">Small</GlassBadge>
        <GlassBadge glow="blue" size="md">Medium</GlassBadge>
        <GlassBadge glow="green" size="lg">Large</GlassBadge>
        <GlassBadge glow="gold" pulse>Pulsing</GlassBadge>
      </div>

      {/* ════════ Table ════════ */}
      <SectionTitle>Table</SectionTitle>
      <GlassPanel blur="lg" className="w-full max-w-5xl">
        <table className="glass-table">
          <thead>
            <tr>
              <th>Ministry</th>
              <th>Score</th>
              <th>Budget</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Education, Arts & Culture</td>
              <td>92%</td>
              <td>N$ 840M</td>
              <td><GlassBadge glow="green" size="sm">On Track</GlassBadge></td>
            </tr>
            <tr>
              <td>Health & Social Services</td>
              <td>78%</td>
              <td>N$ 620M</td>
              <td><GlassBadge glow="gold" size="sm">At Risk</GlassBadge></td>
            </tr>
            <tr>
              <td>Works & Transport</td>
              <td>65%</td>
              <td>N$ 1.1B</td>
              <td><GlassBadge glow="red" size="sm">Delayed</GlassBadge></td>
            </tr>
            <tr>
              <td>Finance & Public Enterprises</td>
              <td>88%</td>
              <td>N$ 450M</td>
              <td><GlassBadge glow="green" size="sm">On Track</GlassBadge></td>
            </tr>
          </tbody>
        </table>
      </GlassPanel>

      {/* ════════ Progress Bars ════════ */}
      <SectionTitle>Progress Bars</SectionTitle>
      <GlassPanel blur="md" className="w-full max-w-5xl space-y-4">
        {[
          { label: 'NDP6 Pillar 1 — Economic', pct: 72, color: 'blue' },
          { label: 'NDP6 Pillar 2 — Social', pct: 58, color: 'green' },
          { label: 'NDP6 Pillar 3 — Environmental', pct: 41, color: 'gold' },
          { label: 'NDP6 Pillar 4 — Governance', pct: 85, color: 'navy' },
        ].map((item) => (
          <div key={item.label}>
            <div className="flex justify-between text-xs text-white/50 mb-1.5">
              <span>{item.label}</span>
              <span className="text-white/70 font-medium">{item.pct}%</span>
            </div>
            <div className="glass-progress">
              <div
                className={`glass-progress__fill glass-progress__fill--${item.color}`}
                style={{ width: `${item.pct}%` }}
              />
            </div>
          </div>
        ))}
      </GlassPanel>

      {/* ════════ Form Inputs ════════ */}
      <SectionTitle>Form Controls</SectionTitle>
      <GlassCard className="w-full max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Project Name</label>
            <GlassInput placeholder="Enter project name..." />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Ministry</label>
            <GlassSelect>
              <option value="">Select ministry...</option>
              <option>Education, Arts & Culture</option>
              <option>Health & Social Services</option>
              <option>Works & Transport</option>
            </GlassSelect>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Search</label>
            <div className="relative">
              <GlassInput placeholder="Search projects..." className="pl-9" />
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Budget (NAD)</label>
            <GlassInput type="number" placeholder="0.00" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Description</label>
            <GlassTextarea placeholder="Enter project description..." />
          </div>
        </div>
      </GlassCard>

      {/* ════════ Buttons ════════ */}
      <SectionTitle>Buttons — Brand Colours</SectionTitle>
      <div className="flex flex-wrap justify-center gap-8">
        <GlassButton glow="red">Read More</GlassButton>
        <GlassButton glow="blue">Read More</GlassButton>
        <GlassButton glow="green">Read More</GlassButton>
      </div>

      <div className="flex flex-wrap justify-center gap-8">
        <GlassButton glow="navy">Navy</GlassButton>
        <GlassButton glow="gold">Gold</GlassButton>
        <GlassButton glow="red">Red</GlassButton>
        <GlassButton glow="green">Green</GlassButton>
        <GlassButton glow="blue">Blue</GlassButton>
      </div>

      <SectionTitle>Buttons — Sizes</SectionTitle>
      <div className="flex flex-wrap items-center justify-center gap-8">
        <GlassButton glow="blue" size="sm">Small</GlassButton>
        <GlassButton glow="blue" size="md">Medium</GlassButton>
        <GlassButton glow="blue" size="lg">Large</GlassButton>
      </div>

      <SectionTitle>Buttons — With Icons</SectionTitle>
      <div className="flex flex-wrap justify-center gap-8">
        <GlassButton glow="green" icon={FiEye}>View Project</GlassButton>
        <GlassButton glow="gold" icon={FiDownload}>Download</GlassButton>
        <GlassButton glow="red" icon={FiSend} iconPosition="right">Submit</GlassButton>
      </div>

      <div className="flex flex-wrap justify-center gap-8">
        <GlassButton glow="navy" href="#" icon={FiArrowRight} iconPosition="right">
          Learn More
        </GlassButton>
        <GlassButton glow="blue" href="#" icon={FiPlus}>
          New Project
        </GlassButton>
      </div>

      {/* ════════ Panels ════════ */}
      <SectionTitle>Panels — Blur Levels</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl">
        <GlassPanel blur="sm">
          <p className="text-xs text-white/50 uppercase tracking-wide mb-1">Light Blur</p>
          <p className="text-sm">Subtle frosted background for layered content areas.</p>
        </GlassPanel>
        <GlassPanel blur="md">
          <p className="text-xs text-white/50 uppercase tracking-wide mb-1">Medium Blur</p>
          <p className="text-sm">Standard frosted panel for most use cases.</p>
        </GlassPanel>
        <GlassPanel blur="lg">
          <p className="text-xs text-white/50 uppercase tracking-wide mb-1">Heavy Blur</p>
          <p className="text-sm">Maximum depth separation for modal overlays.</p>
        </GlassPanel>
      </div>

      {/* ════════ Composed Example ════════ */}
      <SectionTitle>Composed Dashboard Section</SectionTitle>
      <GlassPanel blur="lg" className="w-full max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Project Overview</h3>
            <p className="text-xs text-white/40">FY 2025/26 Quarter 3</p>
          </div>
          <GlassNavbar
            items={[
              { label: 'All' },
              { label: 'Active' },
              { label: 'Delayed' },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <GlassStatCard title="Total" value="62" accent="navy" icon={FiBriefcase} />
          <GlassStatCard title="On Track" value="47" accent="green" trend={8} icon={FiCheckCircle} />
          <GlassStatCard title="Overdue" value="6" accent="red" trend={-2} icon={FiAlertTriangle} />
        </div>

        <div className="glass-divider" />

        <table className="glass-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Ministry</th>
              <th>Progress</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="font-medium text-white/90">Windhoek–Hosea Kutako Highway</td>
              <td>Works & Transport</td>
              <td>
                <div className="glass-progress" style={{ width: 120 }}>
                  <div className="glass-progress__fill glass-progress__fill--blue" style={{ width: '72%' }} />
                </div>
              </td>
              <td><GlassBadge glow="blue" size="sm">In Progress</GlassBadge></td>
            </tr>
            <tr>
              <td className="font-medium text-white/90">School Feeding Programme</td>
              <td>Education</td>
              <td>
                <div className="glass-progress" style={{ width: 120 }}>
                  <div className="glass-progress__fill glass-progress__fill--green" style={{ width: '91%' }} />
                </div>
              </td>
              <td><GlassBadge glow="green" size="sm">On Track</GlassBadge></td>
            </tr>
            <tr>
              <td className="font-medium text-white/90">Rural Electrification Phase 3</td>
              <td>Mines & Energy</td>
              <td>
                <div className="glass-progress" style={{ width: 120 }}>
                  <div className="glass-progress__fill glass-progress__fill--red" style={{ width: '38%' }} />
                </div>
              </td>
              <td><GlassBadge glow="red" size="sm" pulse>Delayed</GlassBadge></td>
            </tr>
          </tbody>
        </table>
      </GlassPanel>

      <div className="h-16" />
    </div>
  );
};

export default GlassButtonDemo;
